import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    QUEUE_TOWARDS_CHARGEBEE,
    QUEUE_TOWARDS_EXACT,
    RequeueItem,
    queueForObjectType,
    routeOf,
} from '../../api/requeue';
import { RequeueProgress } from '../../hooks/useRequeue';
import './Requeue.scss';

export interface IRequeueBarProps {
    /** Number of selected rows. */
    selectedCount: number;
    /** Rows that can be requeued, across the whole list. */
    selectableCount: number;
    /** Rows without an objectGuid; their queue message cannot be reconstructed. */
    blockedCount: number;

    /** The selection itself, so the bar can say where each object is headed. */
    selectedItems: RequeueItem[];

    /** The queues requeueing is allowed to write to. */
    targets: string[];

    /**
     * Which way an ambiguous object should travel. A customer is handled by both send
     * handlers and nothing in the tables says which one changed it, so this is the one
     * choice that genuinely has to be made by a person.
     */
    ambiguousQueue: string;
    onAmbiguousQueueChange: (queueName: string) => void;

    busy: boolean;
    progress: RequeueProgress | null;

    onToggleAll: () => void;
    /** Sends each group to the queue it belongs on, one call per queue. */
    onRequeue: (groups: { queue: string; items: RequeueItem[] }[]) => void;
}

/** What a queue is for, in the words of somebody who has to press the button. */
const QUEUE_PURPOSE: Record<string, string> = {
    [QUEUE_TOWARDS_EXACT]: 'op weg naar Exact',
    [QUEUE_TOWARDS_CHARGEBEE]: 'op weg naar Chargebee',
};

/**
 * The bar above the list.
 *
 * The destination is worked out per object rather than chosen for the batch. That used to
 * be a dropdown, and the dropdown was a trap: both send handlers open with
 * `if (!CanBeProcessed(message)) return;`, after which the function carries on and writes
 * the object into the sync log as successfully synchronised. So a message on the wrong
 * transfer queue is not rejected - it is deleted, and the monitoring says it went fine.
 * There is no way to notice afterwards, which makes "kiezen" the wrong interaction
 * entirely.
 *
 * What is left to choose is the one thing the tables genuinely cannot answer: a customer
 * travels in both directions, and which queue it belongs on follows from which system
 * changed it.
 */
export const RequeueBar = (props: IRequeueBarProps) => {
    const nothingSelected = props.selectedCount === 0;
    const allSelected =
        props.selectableCount > 0 &&
        props.selectedCount === props.selectableCount;

    /*
        The selection split by where it has to go. An object nothing handles gets no group
        at all: there is no queue that would do anything with it, so the honest thing is to
        leave it out and say so rather than to offer a button that cannot work.
    */
    const groups = new Map<string, RequeueItem[]>();
    const unroutable: RequeueItem[] = [];
    let ambiguous = 0;

    for (const item of props.selectedItems) {
        const route = routeOf(item.objectType);

        if (route === 'None') {
            unroutable.push(item);
            continue;
        }

        const queue =
            route === 'Both'
                ? props.ambiguousQueue
                : (queueForObjectType(item.objectType) as string);

        if (route === 'Both') {
            ambiguous++;
        }

        const existing = groups.get(queue);

        if (existing) {
            existing.push(item);
        } else {
            groups.set(queue, [item]);
        }
    }

    const plan = [...groups.entries()].map(([queue, items]) => ({
        queue,
        items,
    }));

    const routable = plan.reduce((total, group) => total + group.items.length, 0);

    return (
        <div className="requeue-bar">
            <label className="requeue-bar__check">
                <input
                    type="checkbox"
                    checked={allSelected}
                    disabled={props.selectableCount === 0}
                    onChange={props.onToggleAll}
                />
                <span>
                    {nothingSelected
                        ? 'Alles selecteren'
                        : `${props.selectedCount} van ${props.selectableCount} geselecteerd`}
                </span>
            </label>

            {props.blockedCount > 0 && (
                <span
                    className="requeue-bar__blocked"
                    title="Zonder objectGuid in de logregel is het oorspronkelijke queuebericht niet te reconstrueren, dus deze regels kunnen niet opnieuw."
                >
                    {props.blockedCount} regel(s) kunnen niet opnieuw
                </span>
            )}

            <div className="requeue-bar__actions">
                {ambiguous > 0 && props.targets.length > 1 && (
                    <label
                        className="requeue-bar__target"
                        title="Een Customer wordt door beide verzendfunctions verwerkt. Welke queue de juiste is hangt af van welk systeem de wijziging deed, en dat staat nergens in de tabellen."
                    >
                        <span>{ambiguous}x Customer naar</span>
                        <select
                            className="form-select filter filter--sm"
                            value={props.ambiguousQueue}
                            onChange={(e) =>
                                props.onAmbiguousQueueChange(e.target.value)
                            }
                        >
                            {props.targets.map((name) => (
                                <option key={name} value={name}>
                                    {name}
                                    {QUEUE_PURPOSE[name]
                                        ? ` (${QUEUE_PURPOSE[name]})`
                                        : ''}
                                </option>
                            ))}
                        </select>
                    </label>
                )}

                <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={routable === 0 || props.busy}
                    onClick={() => props.onRequeue(plan)}
                >
                    {props.busy && (
                        <FontAwesomeIcon icon="spinner" spin className="me-2" />
                    )}
                    Opnieuw aanbieden
                    {routable > 0 && ` (${routable})`}
                </button>
            </div>

            {/*
                Where the selection is headed, before it is sent. Not a confirmation dialog
                and not a tooltip: the split is the thing that used to be invisible, and the
                one place it can prevent a mistake is next to the button that makes it.
            */}
            {!nothingSelected && (
                <div className="requeue-bar__plan">
                    {plan.map((group) => (
                        <span
                            className="requeue-bar__plan-group"
                            key={group.queue}
                            title={`${group.items.length} object(en) gaan naar de queue die ${
                                QUEUE_PURPOSE[group.queue] ?? 'deze objecten'
                            } verwerkt.`}
                        >
                            {group.items.length}
                            <FontAwesomeIcon
                                icon="arrow-right"
                                className="requeue-bar__plan-arrow"
                            />
                            <code>{group.queue}</code>
                            {QUEUE_PURPOSE[group.queue] && (
                                <em>{QUEUE_PURPOSE[group.queue]}</em>
                            )}
                        </span>
                    ))}

                    {unroutable.length > 0 && (
                        <span
                            className="requeue-bar__plan-group requeue-bar__plan-group--none"
                            title={`Geen enkele verzendfunction heeft een handler voor ${[
                                ...new Set(
                                    unroutable.map((item) =>
                                        String(item.objectType)
                                    )
                                ),
                            ].join(
                                ', '
                            )}. Op welke queue dan ook zou het bericht verdwijnen zonder foutregel en als geslaagd in de synclog komen te staan.`}
                        >
                            <FontAwesomeIcon
                                icon="triangle-exclamation"
                                className="me-2"
                            />
                            {unroutable.length} object(en) worden nergens
                            verwerkt en gaan niet mee
                        </span>
                    )}
                </div>
            )}

            {props.busy && props.progress && (
                <div className="requeue-bar__progress">
                    <div
                        className="requeue-bar__progress-fill"
                        style={{
                            width: `${Math.round(
                                (props.progress.done /
                                    Math.max(1, props.progress.total)) *
                                    100
                            )}%`,
                        }}
                    />
                    <span className="requeue-bar__progress-label">
                        {props.progress.done} van {props.progress.total} verstuurd
                    </span>
                </div>
            )}
        </div>
    );
};
